import { SecretField } from '@Components/SecretField';
import { LinkedListWithLayoutSettings } from '@Components/LinkedListWithLayoutSettings';
import { MESSAGES_API } from '@Constants/backendConstants';
import { getFrameLabel } from '@Constants/getFrameLabel';
import { Permission } from '@Constants/Permission.g';
import { mandantColumns } from '@Constants/TableColumns';
import { useHasPermission } from '@Contexts/appContext';
import {
    type ApiScalesToolRegistration,
    type ApiMandant,
    EnumFrontendLayoutSetting,
    EnumUnit,
    unitOptions,
    ScaleToolLinkLists,
} from '@Model';
import { FrameEditMandant } from '@Modules/Permissions';
import {
    Card,
    Checkbox,
    EditFrameMode,
    EditFrameShell,
    FrameEditBase,
    Grid,
    GridColumn,
    GridColumns,
    type IFrameBodyProps,
    type IFrameEditShellProps,
    TextInput,
    Select,
    UnlabeledField,
    useFrameEditBaseNavigation,
    useGuiBinder,
    useLinkedList,
} from '@synqo/frontend-component-library';

import { useParams } from 'react-router';

export function FrameEditScaleToolRegistration(props: IFrameEditShellProps<ApiScalesToolRegistration>) {
    const hasPermission = useHasPermission();
    const { itemId } = useParams();
    const primaryKey = props.primaryKey ?? itemId ?? '';

    const frameEditBaseNavigationEvents = useFrameEditBaseNavigation();

    return (
        <FrameEditBase
            apiResourcePath={`${MESSAGES_API}/scale_tools`}
            renderer={FrameEditScaleToolRegistrationBody}
            key={primaryKey}
            primaryKey={primaryKey}
            embedded={props.embedded || props.primaryKey !== undefined}
            canEdit={props.canEdit !== false && hasPermission(Permission.MessagesScalesRegistrationUpdate)}
            canCreate={props.canCreate !== false && hasPermission(Permission.MessagesScalesRegistrationCreate)}
            canDelete={props.canDelete !== false && hasPermission(Permission.MessagesScalesRegistrationDelete)}
            frameProps={{ events: { ...frameEditBaseNavigationEvents, ...props.events } }}
        />
    );
}

function FrameEditScaleToolRegistrationBody(props: IFrameBodyProps<ApiScalesToolRegistration>) {
    const apiScaleTool = props.data;

    const binder = useGuiBinder<ApiScalesToolRegistration>({
        ...props,
        canEdit: props.mode !== EditFrameMode.View,
    });

    let enumMassUnitOptions = unitOptions.find((o) => o.label === 'Gewichte')?.value;
    if (!Array.isArray(enumMassUnitOptions)) {
        enumMassUnitOptions = [];
    }

    const baseApiObjectPath = `${props.apiResourcePath}/${apiScaleTool.id}`;
    const {
        refetchRef: refetchMandantenRef,
        linkListApi: linkMandantApi,
        linkedObjectOverlayEvents: mandantsOverlayEvents,
    } = useLinkedList<ApiScalesToolRegistration, ApiMandant>(
        baseApiObjectPath,
        ScaleToolLinkLists.mandanten,
        false,
        FrameEditMandant,
    );

    return (
        <EditFrameShell
            headerProps={{
                label: getFrameLabel('Waage-Registrierung', props.mode),
                resourceLabel: 'Waage-Registrierung',
                ...props.events,
            }}
            bannerProps={{
                mode: props.mode,
                loading: props.stateQueueDepth > 0,
                ...props.events,
            }}
        >
            <Card name="MainData" border={'withoutBorder'}>
                <Grid>
                    <GridColumns spans={[4, 4, 4]}>
                        <TextInput {...binder.bindToProp('label')} label={'Verbindungsname'} />
                        <UnlabeledField>
                            <Checkbox {...binder.bindToAutoConfirmed('is_manual')} label={'Händische Waage'} />
                        </UnlabeledField>
                        {apiScaleTool.is_manual && (
                            <Select<EnumUnit>
                                items={enumMassUnitOptions}
                                {...binder.bindToAutoConfirmed('unit')}
                                label="Einheit"
                            />
                        )}
                    </GridColumns>
                    {!apiScaleTool.is_manual && props.mode !== EditFrameMode.Create && (
                        <GridColumn span={12}>
                            <SecretField label={'API-Key'} value={apiScaleTool.api_key} />
                        </GridColumn>
                    )}
                </Grid>
            </Card>

            <Card name="Mandanten" maxWidth={'80rem'}>
                <LinkedListWithLayoutSettings
                    layoutSettingsContext={EnumFrontendLayoutSetting.MandantsTableLayout}
                    layoutSettingsModalContext={EnumFrontendLayoutSetting.MandantsTableLayout}
                    {...binder.bindToLinkList(linkMandantApi)}
                    {...mandantsOverlayEvents}
                    refetchRef={refetchMandantenRef}
                    columns={mandantColumns}
                    label="Mandanten"
                />
            </Card>
        </EditFrameShell>
    );
}
